import { useState, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import PageMeta from '@/components/feature/PageMeta';
import Breadcrumb from '@/components/base/Breadcrumb';
import ComicCard from '@/components/base/ComicCard';
import { categories } from '@/mocks/categories';
import { getComicsByCategory } from '@/mocks/comics';

const ITEMS_PER_PAGE = 12;

export default function CategoryPage() {
  const { slug = 'all' } = useParams<{ slug: string }>();
  const [currentPage, setCurrentPage] = useState(1);

  const currentCategory = categories.find((c) => c.slug === slug) || categories[0];
  const comics = useMemo(() => getComicsByCategory(slug), [slug]);
  const totalPages = Math.ceil(comics.length / ITEMS_PER_PAGE);
  const displayedComics = comics.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE);

  const categoryJsonLd = [
    {
      '@context': 'https://schema.org',
      '@type': 'CollectionPage',
      name: `${currentCategory.name} - 韩漫屋`,
      description: `韩漫屋${currentCategory.name}分类，汇集好看的韩国漫画免费阅读`,
      url: `http://nheap.cn/category/${slug}`,
    },
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: '首页', item: 'http://nheap.cn/' },
        { '@type': 'ListItem', position: 2, name: currentCategory.name },
      ],
    },
  ];

  return (
    <>
      <PageMeta
        title={`${currentCategory.name} - 好看的韩国漫画 - 韩漫屋`}
        description={`韩漫屋${currentCategory.name}分类，汇集了${comics.length}部好看的韩国漫画，支持韩漫免费阅读。通过韩漫网址快速访问韩漫屋${currentCategory.name}专区。`}
        keywords={`好看的韩国漫画, 韩漫免费阅读, 韩漫网址, 韩漫屋, ${currentCategory.name}韩漫`}
        canonicalUrl={`http://nheap.cn/category/${slug}`}
        jsonLd={categoryJsonLd}
      />

      <main className="pt-16 min-h-screen">
        <div className="w-full max-w-7xl mx-auto px-4 md:px-6">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: currentCategory.name },
            ]}
          />

          <div className="mb-6">
            <h1 className="font-heading font-bold text-2xl md:text-3xl text-foreground-900">
              {currentCategory.name}
              <span className="text-base font-normal text-foreground-500 ml-2">共 {comics.length} 部作品</span>
            </h1>
          </div>

          <div className="flex flex-wrap items-center gap-2 mb-8 bg-background-100 rounded-xl p-1.5">
            {categories.map((cat) => (
              <Link
                key={cat.id}
                to={cat.id === 'all' ? '/category/all' : `/category/${cat.slug}`}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all whitespace-nowrap ${
                  slug === cat.slug
                    ? 'bg-primary-500 text-background-50 shadow-sm'
                    : 'text-foreground-600 hover:text-foreground-900 hover:bg-background-200'
                }`}
              >
                <i className={`${cat.icon} mr-1.5 text-xs`}></i>
                {cat.name}
              </Link>
            ))}
          </div>

          {comics.length === 0 ? (
            <div className="text-center py-20">
              <i className="ri-inbox-line text-5xl text-foreground-300"></i>
              <p className="mt-4 text-foreground-500">该分类暂无漫画作品</p>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4 md:gap-5" data-product-shop>
                {displayedComics.map((comic) => {
                  const cat = categories.find((c) => c.id === comic.category);
                  return (
                    <ComicCard
                      key={comic.id}
                      id={comic.id}
                      slug={comic.slug}
                      title={comic.title}
                      author={comic.author}
                      category={comic.category}
                      status={comic.status}
                      rating={comic.rating}
                      views={comic.views}
                      chapters={comic.chapters}
                      coverImage={comic.coverImage}
                      categoryName={cat?.name}
                    />
                  );
                })}
              </div>

              {totalPages > 1 && (
                <div className="flex items-center justify-center gap-2 py-10">
                  <button
                    onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                    className="w-9 h-9 flex items-center justify-center rounded-lg text-sm border border-background-200 text-foreground-600 hover:bg-background-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors cursor-pointer"
                  >
                    <i className="ri-arrow-left-s-line"></i>
                  </button>
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                    <button
                      key={page}
                      onClick={() => setCurrentPage(page)}
                      className={`w-9 h-9 flex items-center justify-center rounded-lg text-sm font-medium transition-all cursor-pointer ${
                        page === currentPage
                          ? 'bg-primary-500 text-background-50'
                          : 'border border-background-200 text-foreground-600 hover:bg-background-100'
                      }`}
                    >
                      {page}
                    </button>
                  ))}
                  <button
                    onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                    disabled={currentPage === totalPages}
                    className="w-9 h-9 flex items-center justify-center rounded-lg text-sm border border-background-200 text-foreground-600 hover:bg-background-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors cursor-pointer"
                  >
                    <i className="ri-arrow-right-s-line"></i>
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </main>
    </>
  );
}